
import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionErrorProvider } from './context/SubscriptionErrorContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { ThemeProvider } from './context/ThemeContext';
import { ImpersonationProvider, useImpersonation } from './context/ImpersonationContext';
import { LocaleProvider } from './context/LocaleContext';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ConversationsPage from './pages/ConversationsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import ConfigurationPage from './pages/ConfigurationPage';
import ApiKeysPage from './pages/ApiKeysPage';
import DataManagementPage from './pages/DataManagementPage';
import AnalyticsPage from './pages/AnalyticsPage';
import DashboardPreferencesPage from './pages/DashboardPreferencesPage';
import DataManagementPreferencesPage from './pages/DataManagementPreferencesPage';
import AIConfigurationPage from './pages/AIConfigurationPage';
import OAuthCallbackHandler from './components/analytics/OAuthCallbackHandler';
// Admin Pages
import TenantsPage from './pages/admin/TenantsPage';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import AllUsersPage from './pages/admin/AllUsersPage';
import AllSubscriptionsPage from './pages/admin/AllSubscriptionsPage';
import SubscriptionInsightsPage from './pages/admin/SubscriptionInsightsPage';
import SystemStatsPage from './pages/admin/SystemStatsPage';
import AllApiKeysPage from './pages/admin/AllApiKeysPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  if (user.type !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }
  return <>{children}</>;
};

// Route guard that requires super admin to be impersonating a tenant
const TenantRequiredRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isImpersonating, isSuperAdmin } = useImpersonation();
  
  // Regular users can access tenant pages
  if (!isSuperAdmin) {
    return <>{children}</>;
  }
  
  // Super admin must be impersonating to see tenant pages
  if (!isImpersonating) {
    return <Navigate to="/admin/dashboard" />;
  }
  
  return <>{children}</>;
};

// Route guard for super admin only pages (even when impersonating)
const SuperAdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Only super admin can access
  if (user.type !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LocaleProvider>
          <ImpersonationProvider>
            <SubscriptionErrorProvider>
              <AnalyticsProvider>
                <AppRoutes />
              </AnalyticsProvider>
            </SubscriptionErrorProvider>
          </ImpersonationProvider>
        </LocaleProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

const AppRoutes: React.FC = () => {
    const { user } = useAuth();
    const { isSuperAdmin, isImpersonating } = useImpersonation();
    
    // Determine the default landing page
    const getDefaultRoute = () => {
      if (isSuperAdmin && !isImpersonating) {
        return '/admin/dashboard';
      }
      return '/dashboard';
    };

    return (
        <HashRouter>
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route 
                    path="/*"
                    element={
                        <ProtectedRoute>
                            <MainLayout>
                                <Routes>
                                    {/* Default route based on user type */}
                                    <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
                                    
                                    {/* Tenant pages - require impersonation for super admin */}
                                    <Route path="/dashboard" element={
                                      <TenantRequiredRoute>
                                        <DashboardPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/conversations" element={
                                      <TenantRequiredRoute>
                                        <ConversationsPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/subscriptions" element={
                                      <TenantRequiredRoute>
                                        <SubscriptionsPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/configuration" element={
                                      <TenantRequiredRoute>
                                        <ConfigurationPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/api-keys" element={
                                      <TenantRequiredRoute>
                                        <ApiKeysPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/data-management" element={
                                      <TenantRequiredRoute>
                                        <DataManagementPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/analytics" element={
                                      <TenantRequiredRoute>
                                        <AnalyticsPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/ai-configuration" element={
                                      <SuperAdminOnlyRoute>
                                        <AIConfigurationPage />
                                      </SuperAdminOnlyRoute>
                                    } />
                                    <Route path="/preferences/dashboard" element={
                                      <TenantRequiredRoute>
                                        <DashboardPreferencesPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/preferences/data-management" element={
                                      <TenantRequiredRoute>
                                        <DataManagementPreferencesPage />
                                      </TenantRequiredRoute>
                                    } />
                                    <Route path="/analytics/callback/zoho" element={<OAuthCallbackHandler provider="zoho" />} />
                                    <Route path="/analytics/callback/powerbi" element={<OAuthCallbackHandler provider="powerbi" />} />
                                    
                                    {/* Admin Routes - Protected for super_admin only */}
                                    <Route 
                                        path="/admin/dashboard" 
                                        element={
                                            <AdminRoute>
                                                <SuperAdminDashboard />
                                            </AdminRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/admin/tenants" 
                                        element={
                                            <AdminRoute>
                                                <TenantsPage />
                                            </AdminRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/admin/users" 
                                        element={
                                            <AdminRoute>
                                                <AllUsersPage />
                                            </AdminRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/admin/subscriptions" 
                                        element={
                                            <AdminRoute>
                                                <AllSubscriptionsPage />
                                            </AdminRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/admin/subscriptions/insights" 
                                        element={
                                            <AdminRoute>
                                                <SubscriptionInsightsPage />
                                            </AdminRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/admin/system-stats" 
                                        element={
                                            <AdminRoute>
                                                <SystemStatsPage />
                                            </AdminRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/admin/api-keys" 
                                        element={
                                            <AdminRoute>
                                                <AllApiKeysPage />
                                            </AdminRoute>
                                        } 
                                    />
                                    
                                    <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
                                </Routes>
                            </MainLayout>
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </HashRouter>
    );
}

export default App;
